/**
 * Collapsed board groups (founder soak: "Did not extend role" on the talent
 * board forces every candidate on screen).
 *
 * A group here is a board COLUMN (a pipeline stage): collapsing hides the
 * group's cards, leaving the header with its count chip. Like laneSort, this
 * is a PER-USER view preference persisted in localStorage — how one person
 * likes their board folded, never shared team state.
 *
 * The storage key scopes tenant + user + BOARD (the process key), because
 * stage keys are only unique within one board — two boards can both have an
 * "applied" column and must not fold together. (laneSort predates multi-board
 * and keys stage-wide; this module keys per board from day one.)
 *
 * Pure functions + a storage seam (Pick<Storage, ...>) so everything is
 * unit-testable without jsdom localStorage fuss.
 */

import { LANE_COLLAPSE_KEY_PREFIX } from '@/lib/keptAcrossSignOut';

/** Per tenant + user + board so two staff (or two boards) never collide. */
export function laneCollapseStorageKey(tenantSlug: string, userId: string, boardKey: string): string {
  return `${LANE_COLLAPSE_KEY_PREFIX}${tenantSlug}.${userId}.${boardKey}`;
}

/** Defensive parse — a missing/corrupt blob degrades to nothing collapsed. */
export function loadCollapsedLanes(storage: Pick<Storage, 'getItem'>, key: string): ReadonlySet<string> {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return new Set();
  }
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

export function saveCollapsedLanes(
  storage: Pick<Storage, 'setItem'>,
  key: string,
  collapsed: ReadonlySet<string>,
): void {
  try {
    storage.setItem(key, JSON.stringify([...collapsed].sort()));
  } catch {
    // Quota/private-mode failures degrade to session-only collapse state.
  }
}

/** The set with `stageKey` flipped. Never mutates the input. */
export function toggleCollapsedLane(prev: ReadonlySet<string>, stageKey: string): ReadonlySet<string> {
  const next = new Set(prev);
  if (next.has(stageKey)) next.delete(stageKey);
  else next.add(stageKey);
  return next;
}
